import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Theme,
  Radio,
  RadioGroup,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FilterOptionsState,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { Autocomplete } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  enrollAttendee,
  fetchUsers,
  fetchAttendees,
  fetchSchedules,
} from "../../../redux/instructor/instructorOperations";
import { User, Attendee, Schedule, Class } from "../../../redux/types/types";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { getProfile } from "../../../redux/auth/authOperations";
import LatinTextField from "../../../helpers/latinTextField";
import EmailTextField from "../../../helpers/EmailTextField";
import "react-phone-number-input/style.css";
import { isValidPostalCode } from "postal-code-validator";
import PhoneInput from "react-phone-number-input";
import CustomTextField from "../../CustomTextField/CustomTextField";
import Notiflix from "notiflix";
import {
  getAttendeeImportantInfoApi,
  updateAttendeeImportantInfoApi,
} from "../../../utils/api/attendees";

interface EnrollmentModalProps {
  open: boolean;
  modalClose: any;
  onClose: () => void;
  classItem: Class | null;
  onEnrollmentSuccess?: () => void;
  isAttendance?: boolean;
  isAdmin?: boolean;
}

type UserData =
  | number
  | {
      email: string;
      firstName?: string;
      firstname?: string;
      lastName?: string;
      lastname?: string;
      phone?: string;
      dataUsageAgreed: boolean;
      photoAgreed: boolean;
    };
type AttendeeData =
  | number
  | {
      firstName: string;
      lastName: string;
      birthdate: string | Date;
      sex: "male" | "female" | "other";
      phone: string;
      email: string;
      postalCode: string;
      permisStatus: string;
      importantInfo?: string | null;
    };

interface FormValues {
  user: UserData | null;
  attendee: AttendeeData | null;
  scheduleIds: number[];
}

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  open,
  onClose,
  classItem,
  modalClose,
  onEnrollmentSuccess,
  isAttendance,
  isAdmin = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const intl = useIntl();

  // Get user role information from Redux state
  const authState = useSelector((state: RootState) => state.auth);
  const isInstructor = authState.isInstructor;
  const isAdminFromState = authState.isAdmin;

  // Determine the user role for notifications
  const userRole = isAdminFromState
    ? "admin"
    : isInstructor
      ? "instructor"
      : "user";

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  // Safari browser detection
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const [users, setUsers] = useState<User[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(
    null,
  );
  const [isNewUser, setIsNewUser] = useState(false);
  const [isNewAttendee, setIsNewAttendee] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [isWaitlistAlert, setIsWaitlistAlert] = useState(false);
  const [isPaymentRequired, setIsPaymentRequired] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [date, setDate] = useState<string | null>(null);
  const [showOldSchedules, setShowOldSchedules] = useState(false);
  const [importantInfo, setImportantInfo] = useState("");
  const [importantInfoChanged, setImportantInfoChanged] = useState(false);

  // Safari-specific state refresh helper
  const refreshSafariAccordionState = useCallback(() => {
    if (isSafari) {
      setTimeout(() => {
        setActiveStep(activeStep);
      }, 0);
    }
  }, [isSafari, activeStep]);
  // const [time, setTime] = useState<string | null>(null);

  const clearAllStates = () => {
    setSelectedUser(null);
    setSelectedAttendee(null);
    setIsNewUser(false);
    setIsNewAttendee(false);
    setBackendError(null);
    setIsWaitlistAlert(false);
    setIsPaymentRequired(false);
    setActiveStep(0);
    setAttendees([]);
    setSchedules([]);
    setDate(null);
    setShowOldSchedules(false);
    setImportantInfo("");
    setImportantInfoChanged(false);
    // setTime(null);
  };

  useEffect(() => {
    if (open && classItem) {
      // Clear all previous states when modal opens
      clearAllStates();

      dispatch(fetchUsers()).then((action) => {
        if (fetchUsers.fulfilled.match(action)) {
          setUsers(action.payload);
        }
      });
      dispatch(fetchSchedules(classItem.id)).then((action) => {
        if (fetchSchedules.fulfilled.match(action)) {
          setSchedules(action.payload);
        }
      });
    }
  }, [open, classItem, dispatch]);

  useEffect(() => {
    if (selectedUser && selectedUser.id) {
      dispatch(fetchAttendees({ userId: selectedUser.id })).then((action) => {
        if (fetchAttendees.fulfilled.match(action)) {
          setAttendees(action.payload);
        }
      });
    }
  }, [selectedUser, dispatch]);

  const validationSchema = Yup.object().shape({
    user: Yup.mixed().required(
      intl.formatMessage({ id: "enrollmentModal.userRequired" }),
    ),
    attendee: Yup.mixed().required(
      intl.formatMessage({ id: "enrollmentModal.attendeeRequired" }),
    ),
    scheduleIds: Yup.array()
      .of(Yup.number())
      .min(1, intl.formatMessage({ id: "enrollmentModal.scheduleRequired" })),
  });

  const initialValues: FormValues = {
    user: null,
    attendee: null,
    scheduleIds: [],
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    setBackendError(null);
    setIsWaitlistAlert(false);
    if (values.user !== null && values.attendee !== null) {
      const attendeeData =
        typeof values.attendee === "number"
          ? values.attendee
          : {
              ...values.attendee,
              birthdate: new Date(values.attendee.birthdate),
            };

      try {
        if (typeof values.attendee === "number" && importantInfoChanged) {
          await updateAttendeeImportantInfoApi(
            values.attendee,
            importantInfo || null,
          );
        }

        let from = date
          ? new Date(new Date(date).setHours(0, 0))
          : new Date(new Date().setHours(0, 0));
        // if (time) {
        //   const [hours, minutes] = time.split(":").map(Number);
        //   from.setHours(hours);
        //   from.setMinutes(minutes);
        // }
        const result = await dispatch(
          enrollAttendee({
            user: values.user,
            attendee: attendeeData,
            scheduleIds: values.scheduleIds,
            language: intl.locale,
            from,
          }),
        ).unwrap();
        await dispatch(getProfile());

        console.log("Enrollment result:", result);
        console.log("Is admin:", isAdmin);
        console.log("User role:", userRole);
        console.log("Pending enrollment:", result.pendingEnrollment);

        // Handle payment-required scenario with role-specific notifications
        if (result.pendingEnrollment && !isAdmin) {
          console.log("Showing payment required notification for regular user");
          // Show role-specific notification for pending enrollment
          const paymentMessageKey = `enrollment.paymentRequired.${userRole}`;
          Notiflix.Notify.warning(
            intl.formatMessage({ id: paymentMessageKey }),
            { timeout: 6000 },
          );
          // Close the enrollment modal first
          clearAllStates();
          onClose();
          // Then show payment dialog
          setIsPaymentRequired(true);
          return;
        }

        // For admin users, still show notification but don't redirect to payment
        if (result.pendingEnrollment && isAdmin) {
          console.log("Showing payment required notification for admin user");
          // Admins get specific admin message
          Notiflix.Notify.warning(
            intl.formatMessage({ id: "enrollment.paymentRequired.admin" }),
            { timeout: 6000 },
          );
        }

        // Handle attendance mode notifications
        if (Boolean(isAttendance)) {
          if (!result.waitingListEntries) {
            Notiflix.Notify.success(
              intl.formatMessage({ id: "enrollmentModal.attendanceSuccess" }),
              { timeout: 3000 },
            );
          } else {
            Notiflix.Notify.warning(
              intl.formatMessage({ id: "enrollmentModal.addedToWaitingList" }),
              { timeout: 3000 },
            );
          }
        } else {
          // Handle regular enrollment mode notifications
          if (
            (result.waitingListEntries &&
              result.waitingListEntries.length > 0) ||
            result.message ===
              "One or more schedules are full; you have been added to the waiting list for the corresponding classes."
          ) {
            // Show waitlist notification
            Notiflix.Notify.warning(
              intl.formatMessage({ id: "enrollmentModal.addedToWaitingList" }),
              { timeout: 3000 },
            );
            setIsWaitlistAlert(true);
          } else {
            // Show success notification for regular enrollment
            Notiflix.Notify.success(
              intl.formatMessage({ id: "enrollmentModal.enrollmentSuccess" }),
              { timeout: 3000 },
            );
          }
        }

        if (onEnrollmentSuccess) {
          onEnrollmentSuccess();
        }
        clearAllStates();
        onClose();
      } catch (error: any) {
        let errorMessage = intl.formatMessage({
          id: "enrollmentModal.generalError",
        });
        console.log(error);

        if (error) {
          errorMessage = getErrorMessageKey(error);
        }

        const finalErrorMessage = intl.formatMessage({ id: errorMessage });
        setBackendError(finalErrorMessage);

        // Also show error notification
        Notiflix.Notify.failure(finalErrorMessage, { timeout: 5000 });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const getErrorMessageKey = (error: string | string[]): string => {
    const errorMsg = Array.isArray(error) ? error[0] : error;

    if (errorMsg.includes("Attendee is already enrolled in all schedules")) {
      return "enrollmentModal.alreadyEnrolledInAll";
    } else if (errorMsg.includes("Attendee is already enrolled")) {
      return "enrollmentModal.alreadyEnrolled";
    } else if (errorMsg.includes("One or more schedules are full")) {
      return "enrollmentModal.schedulesFull";
    } else if (errorMsg.includes("One or more schedules not found")) {
      return "enrollmentModal.schedulesNotFound";
    } else if (
      errorMsg.includes("A unique constraint violation occurred on email.")
    ) {
      return "attendees.emailAlreadyExists";
    } else if (errorMsg.includes("User with email ")) {
      return "error.emailInUse";
    } else {
      return "enrollmentModal.generalError";
    }
  };

  const renderSchedules = (
    scheduleIds: number[],
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const weekdays = [
      intl.formatMessage({ id: "days.monday" }),
      intl.formatMessage({ id: "days.tuesday" }),
      intl.formatMessage({ id: "days.wednesday" }),
      intl.formatMessage({ id: "days.thursday" }),
      intl.formatMessage({ id: "days.friday" }),
      intl.formatMessage({ id: "days.saturday" }),
      intl.formatMessage({ id: "days.sunday" }),
    ];
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const groupedSchedules = weekdays
      .map((day, index) => ({
        day,
        schedules: schedules.filter((schedule) => {
          if (
            schedule.deactivationDate &&
            new Date(schedule.deactivationDate) < new Date()
          ) {
            return false;
          }

          if (!showOldSchedules && schedule.date) {
            const scheduleDate = new Date(schedule.date);
            if (scheduleDate < oneWeekAgo) {
              return false;
            }
          }

          if (schedule.date) {
            return (
              (new Date(schedule.date).getUTCDay() === 0
                ? 6
                : new Date(schedule.date).getUTCDay() - 1) === index
            );
          }

          return schedule.dayOfWeek === index;
        }),
      }))
      .filter(({ schedules }) => schedules.length > 0);

    return groupedSchedules.map(({ day, schedules }) => (
      <Box key={day} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{day}</Typography>
        {schedules.map((schedule) => (
          <FormControlLabel
            key={schedule.id}
            control={
              <>
                <Checkbox
                  checked={scheduleIds.includes(schedule.id)}
                  onChange={(e) => {
                    const newScheduleIds = e.target.checked
                      ? [...scheduleIds, schedule.id]
                      : scheduleIds.filter((id) => id !== schedule.id);
                    setFieldValue("scheduleIds", newScheduleIds);
                  }}
                />
                {schedule.date && (
                  <FormattedDate
                    value={schedule.date}
                    year="numeric"
                    month="long"
                    day="numeric"
                  />
                )}
              </>
            }
            label={`${schedule.date ? "/" : ""}${schedule.startTime} - ${
              schedule.endTime
            }`}
          />
        ))}
      </Box>
    ));
  };

  const validateStep = (step: number, values: FormValues, errors: any) => {
    switch (step) {
      case 0:
        if (isNewUser) {
          const result =
            !errors.user &&
            values.user &&
            typeof values.user === "object" &&
            values.user.email &&
            values.user.firstName &&
            values.user.lastName &&
            values.user.phone &&
            values.user.dataUsageAgreed;
          return result;
        }
        const step0Result = values.user !== null;
        return step0Result;
      case 1:
        if (!values.user) {
          return false;
        }
        if (isNewAttendee) {
          // For accordion navigation, just check that attendee object exists
          // Full validation will be enforced on form submission
          const step1NewResult =
            values.attendee && typeof values.attendee === "object";
          return step1NewResult;
        }
        const step1ExistingResult =
          values.attendee !== null &&
          (typeof values.attendee === "number" ||
            (typeof values.attendee === "object" &&
              Object.keys(values.attendee).length > 0));
        return step1ExistingResult;
      case 2:
        const step2Result = values.scheduleIds.length > 0;
        return step2Result;
      default:
        return true;
    }
  };

  // Safari-compatible function to check if a step can be accessed
  const canAccessStep = useCallback(
    (stepIndex: number, values: FormValues, errors: any) => {
      if (stepIndex <= activeStep) return true;
      if (stepIndex - activeStep > 1) return false;

      // Special case: when creating new attendee, explicitly enable schedules step
      if (stepIndex === 2 && isNewAttendee && values.user && values.attendee) {
        return true;
      }

      // For Safari compatibility, use a more explicit validation approach
      for (let i = 0; i < stepIndex; i++) {
        if (!validateStep(i, values, errors)) {
          return false;
        }
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStep, isNewUser, isNewAttendee, selectedUser, selectedAttendee],
  );

  const customFilterOptions = (
    options: User[],
    { inputValue }: FilterOptionsState<User>,
  ): User[] => {
    const query = inputValue.toLowerCase().trim();
    if (!query) return options;

    const queryTokens = query.split(/\s+/);

    return options.filter((user) => {
      const userFirstName = user.firstname ? user.firstname.toLowerCase() : "";
      const userLastName = user.lastname ? user.lastname.toLowerCase() : "";
      const fullName = `${userFirstName} ${userLastName}`.trim();

      const email = user.email ? user.email.toLowerCase() : "";

      const userMatch = queryTokens.every(
        (token) => fullName.includes(token) || email.includes(token),
      );

      const attendeesMatch = user.attendees?.some((attendee) => {
        const attendeeFirstName = attendee.firstName
          ? attendee.firstName.toLowerCase()
          : "";
        const attendeeLastName = attendee.lastName
          ? attendee.lastName.toLowerCase()
          : "";
        const attendeeFullName =
          `${attendeeFirstName} ${attendeeLastName}`.trim();
        return queryTokens.every((token) => attendeeFullName.includes(token));
      });

      return userMatch || attendeesMatch;
    });
  };

  const filterOptions = (
    options: User[],
    state: FilterOptionsState<User>,
  ): User[] => {
    return customFilterOptions(options, state);
  };

  const steps = [
    {
      title: intl.formatMessage({ id: "enrollmentModal.selectUser" }),
      content: (
        values: FormValues,
        setFieldValue: (field: string, value: any) => void,
        touched: any,
        errors: any,
      ) => (
        <>
          {!isNewUser ? (
            <>
              <Autocomplete<User>
                options={users}
                filterOptions={filterOptions}
                noOptionsText={intl.formatMessage({ id: "common.noOptions" })}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname} (${option.email})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({
                      id: "enrollmentModal.selectUser",
                      defaultMessage: "Select User",
                    })}
                    fullWidth
                  />
                )}
                onChange={(_, value: User | null) => {
                  setSelectedUser(value);
                  setFieldValue("user", value ? value.id : null);
                  setSelectedAttendee(null);
                  setFieldValue("attendee", null);
                  setIsNewAttendee(false);

                  if (value && value.id) {
                    dispatch(fetchAttendees({ userId: value.id })).then(
                      (action: any) => {
                        if (fetchAttendees.fulfilled.match(action)) {
                          setAttendees(action.payload);
                        }
                      },
                    );
                  } else {
                    setAttendees([]);
                  }
                }}
                value={selectedUser}
              />
              <Button
                onClick={() => {
                  setIsNewUser(true);
                  setSelectedUser(null);
                  setAttendees([]);
                  setSelectedAttendee(null);
                }}
                sx={{ mt: 1 }}
              >
                <FormattedMessage
                  id="enrollmentModal.createNewUser"
                  defaultMessage="Create New User"
                />
              </Button>
            </>
          ) : (
            <>
              <Field
                as={EmailTextField}
                name="user.email"
                label={intl.formatMessage({ id: "signUp.email" })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .email(intl.formatMessage({ id: "signUp.invalidEmail" }))
                    .required(
                      intl.formatMessage({ id: "signUp.emailRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <Field
                as={LatinTextField}
                name="user.firstName"
                label={intl.formatMessage({
                  id: "signUp.firstName.input",
                })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .required(
                      intl.formatMessage({ id: "signUp.firstNameRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <Field
                as={LatinTextField}
                name="user.lastName"
                label={intl.formatMessage({
                  id: "signUp.lastName.input",
                })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .required(
                      intl.formatMessage({ id: "signUp.lastNameRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <div style={{ margin: "16px 0" }}>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="US"
                  value={
                    (typeof values.user === "object" && values.user?.phone) ||
                    ""
                  }
                  onChange={(value) => setFieldValue("user.phone", value)}
                  inputComponent={CustomTextField as any}
                  error={typeof values.user === "object" && errors.user?.phone}
                />
                {errors.user?.phone && (
                  <Typography color="error" variant="caption">
                    {errors.user?.phone}
                  </Typography>
                )}
              </div>
              {/* <Field name="user.dataUsageAgreed"> */}
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        typeof values.user === "object" &&
                        values.user?.dataUsageAgreed
                      }
                      onChange={() =>
                        setFieldValue(
                          "user.dataUsageAgreed",
                          typeof values.user === "object" &&
                            !values.user?.dataUsageAgreed,
                        )
                      }
                      color="primary"
                    />
                  }
                  label={intl.formatMessage({
                    id: "signUp.dataUsageAgreed",
                  })}
                />
                {touched.user?.dataUsageAgreed &&
                  errors.user?.dataUsageAgreed && (
                    <FormHelperText error>
                      {errors.user?.dataUsageAgreed}
                    </FormHelperText>
                  )}
              </div>
              {/* </Field> */}
              {/* <Field name="user.photoAgreed"> */}
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        typeof values.user === "object" &&
                        values.user?.photoAgreed
                      }
                      onChange={() =>
                        setFieldValue(
                          "user.photoAgreed",
                          typeof values.user === "object" &&
                            !values.user?.photoAgreed,
                        )
                      }
                      color="primary"
                    />
                  }
                  label={intl.formatMessage({ id: "signUp.photoAgreed" })}
                />
                {touched.user?.photoAgreed && errors.user?.photoAgreed && (
                  <FormHelperText error>
                    {errors.user?.photoAgreed}
                  </FormHelperText>
                )}
              </div>
              {/* </Field> */}
              <Button
                onClick={() => {
                  setIsNewUser(false);
                  setFieldValue("user", null);
                  setSelectedAttendee(null);
                  setFieldValue("attendee", null);
                  setIsNewAttendee(false);
                  setAttendees([]);
                }}
                sx={{ mt: 1 }}
              >
                <FormattedMessage
                  id="enrollmentModal.selectExistingUser"
                  defaultMessage="Select Existing User"
                />
              </Button>
            </>
          )}
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: "enrollmentModal.selectAttendee" }),
      content: (
        values: FormValues,
        setFieldValue: (field: string, value: any) => void,
        touched: any,
        errors: any,
      ) => (
        <>
          {!isNewAttendee ? (
            <>
              <Autocomplete
                options={[
                  {
                    id: "userData",
                    firstName: "",
                    lastName: "",
                    email: "",
                  },
                  ...attendees,
                ]}
                getOptionLabel={(option) =>
                  option.id === "userData"
                    ? intl.formatMessage({
                        id: "enrollmentModal.userData",
                      })
                    : `${option.firstName} ${option.lastName} (${option.email})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({
                      id: "enrollmentModal.selectAttendee",
                      defaultMessage: "Select Attendee",
                    })}
                    fullWidth
                  />
                )}
                onChange={(_, value) => {
                  if (value?.id === "userData") {
                    const userData = isNewUser
                      ? values.user
                      : users.find((u) => u.id === values.user) || {};

                    const userAttendee = attendees.find(
                      (attendee) => attendee.isUser === true,
                    );

                    if (userAttendee) {
                      setSelectedAttendee(userAttendee);
                      setFieldValue("attendee", userAttendee.id);
                      setIsNewAttendee(false);
                      setImportantInfoChanged(false);
                      getAttendeeImportantInfoApi(userAttendee.id)
                        .then((response) =>
                          setImportantInfo(response.data.importantInfo || ""),
                        )
                        .catch(() => setImportantInfo(""));
                    } else {
                      setIsNewAttendee(true);
                      setSelectedAttendee(null);
                      setFieldValue("attendee", {
                        firstName:
                          (userData as any)?.firstname ||
                          (userData as any)?.firstName ||
                          "",
                        lastName:
                          (userData as any)?.lastname ||
                          (userData as any)?.lastName ||
                          "",
                        email: (userData as any)?.email || "",
                        phone: (userData as any)?.phone || "",
                        birthdate: "",
                        sex: "other",
                        postalCode: "",
                        permisStatus: "",
                        importantInfo: "",
                      });
                      setImportantInfo("");
                      setImportantInfoChanged(false);
                    }
                    // Safari-specific: Force re-evaluation of accordion state
                    refreshSafariAccordionState();
                  } else if (value) {
                    setSelectedAttendee(value as Attendee);
                    setFieldValue("attendee", value.id);
                    setIsNewAttendee(false);
                    setImportantInfoChanged(false);
                    getAttendeeImportantInfoApi(Number(value.id))
                      .then((response) =>
                        setImportantInfo(response.data.importantInfo || ""),
                      )
                      .catch(() => setImportantInfo(""));
                    // Safari-specific: Force re-evaluation of accordion state
                    refreshSafariAccordionState();
                  } else {
                    setSelectedAttendee(null);
                    setFieldValue("attendee", null);
                    setIsNewAttendee(false);
                    setImportantInfo("");
                    setImportantInfoChanged(false);
                    // Safari-specific: Force re-evaluation of accordion state
                    refreshSafariAccordionState();
                  }
                }}
                value={selectedAttendee}
                disabled={!selectedUser && !isNewUser}
              />
              <Button
                onClick={() => {
                  setIsNewAttendee(true);
                  setSelectedAttendee(null);
                  setFieldValue("attendee", {
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    birthdate: "",
                    sex: "other",
                    postalCode: "",
                    permisStatus: "",
                    importantInfo: "",
                  });
                  setImportantInfo("");
                  setImportantInfoChanged(false);
                  // Safari-specific: Force re-evaluation of accordion state
                  refreshSafariAccordionState();
                }}
                sx={{ mt: 1 }}
                disabled={!selectedUser && !isNewUser}
              >
                <FormattedMessage
                  id="enrollmentModal.createNewAttendee"
                  defaultMessage="Create New Attendee"
                />
              </Button>
              {selectedAttendee && (
                <TextField
                  value={importantInfo}
                  onChange={(event) => {
                    setImportantInfo(event.target.value);
                    setImportantInfoChanged(true);
                  }}
                  label={intl.formatMessage({
                    id: "attendee.importantInfo.label",
                  })}
                  helperText={intl.formatMessage({
                    id: "attendee.importantInfo.helper",
                  })}
                  multiline
                  minRows={3}
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 4000 }}
                />
              )}
            </>
          ) : (
            <>
              <Field
                as={LatinTextField}
                name="attendee.firstName"
                label={intl.formatMessage({
                  id: "signUp.firstName.input",
                })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .required(
                      intl.formatMessage({ id: "signUp.firstNameRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <Field
                as={LatinTextField}
                name="attendee.lastName"
                label={intl.formatMessage({
                  id: "signUp.lastName.input",
                })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .required(
                      intl.formatMessage({ id: "signUp.lastNameRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <Field
                as={TextField}
                name="attendee.birthdate"
                label={intl.formatMessage({
                  id: "admin.dashboard.birthdate",
                })}
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                required
              />
              <Field as={RadioGroup} name="attendee.sex" row required>
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "privateEnroll.female",
                  })}
                />
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "privateEnroll.male",
                  })}
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label={intl.formatMessage({
                    id: "privateEnroll.other",
                  })}
                />
              </Field>
              <div style={{ margin: "16px 0" }}>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="US"
                  value={
                    (typeof values.attendee === "object" &&
                      values.attendee?.phone) ||
                    ""
                  }
                  onChange={(value) => setFieldValue("attendee.phone", value)}
                  inputComponent={CustomTextField as any}
                  error={
                    typeof values.attendee === "object" &&
                    errors.attendee?.phone
                  }
                />
                {errors.attendee?.phone && (
                  <Typography color="error" variant="caption">
                    {errors.attendee?.phone}
                  </Typography>
                )}
              </div>
              <Field
                as={EmailTextField}
                name="attendee.email"
                label={intl.formatMessage({ id: "signUp.email" })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .email(intl.formatMessage({ id: "signUp.invalidEmail" }))
                    .required(
                      intl.formatMessage({ id: "signUp.emailRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <Field
                as={TextField}
                name="attendee.postalCode"
                label={intl.formatMessage({
                  id: "signUp.postalCode",
                })}
                fullWidth
                margin="normal"
                required
                validate={(value: any) =>
                  Yup.string()
                    .test(
                      "is-valid-postal-code",
                      intl.formatMessage({
                        id: "attendees.invalidPostalCodeFormat",
                      }),
                      (value) => {
                        if (!value) return false;
                        return isValidPostalCode(value);
                      },
                    )
                    .required(
                      intl.formatMessage({ id: "signUp.postalCodeRequired" }),
                    )
                    .validate(value)
                    .then(() => undefined)
                    .catch((err) => err.message)
                }
              />
              <div style={{ margin: "16px 0" }}>
                <FormControl
                  fullWidth
                  required
                  error={touched.permisStatus && Boolean(errors.permisStatus)}
                >
                  <InputLabel id="permisStatus-label">
                    {intl.formatMessage({
                      id: "attendee.permisStatus",
                    })}
                  </InputLabel>
                  <Select
                    labelId="permisStatus-label"
                    id="permisStatus"
                    label={intl.formatMessage({
                      id: "attendee.permisStatus",
                    })}
                    defaultValue=""
                    onChange={(e) => {
                      setFieldValue("attendee.permisStatus", e.target.value);
                    }}
                  >
                    <MenuItem value="PermisS">
                      {intl.formatMessage({
                        id: "attendee.permisStatus.permisS",
                      })}
                    </MenuItem>
                    <MenuItem value="Other">
                      {intl.formatMessage({
                        id: "attendee.permisStatus.other",
                      })}
                    </MenuItem>
                  </Select>
                  {touched.permisStatus && errors.permisStatus && (
                    <FormHelperText>{errors.permisStatus}</FormHelperText>
                  )}
                </FormControl>
              </div>
              <Field
                as={TextField}
                name="attendee.importantInfo"
                label={intl.formatMessage({
                  id: "attendee.importantInfo.label",
                })}
                helperText={intl.formatMessage({
                  id: "attendee.importantInfo.helper",
                })}
                multiline
                minRows={3}
                fullWidth
                margin="normal"
                inputProps={{ maxLength: 4000 }}
              />
              <Button
                onClick={() => {
                  setIsNewAttendee(false);
                  setSelectedAttendee(null);
                  setFieldValue("attendee", null);
                  // Safari-specific: Force re-evaluation of accordion state
                  refreshSafariAccordionState();
                }}
                sx={{ mt: 1 }}
              >
                <FormattedMessage
                  id="enrollmentModal.selectExistingAttendee"
                  defaultMessage="Select Existing Attendee"
                />
              </Button>
            </>
          )}
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: "enrollmentModal.selectSchedules" }),
      content: (
        values: FormValues,
        setFieldValue: (field: string, value: any) => void,
        touched: any,
        errors: any,
      ) => (
        <>
          <div style={{ marginBottom: "20px" }}>
            <Typography>
              {intl.formatMessage({ id: "admin.enrollmentModal.date" })}:
            </Typography>
            <TextField
              label={intl.formatMessage({ id: "admin.dashboard.date" })}
              type="date"
              value={date}
              InputLabelProps={{ shrink: true }}
              inputProps={
                classItem?.cancelDate
                  ? {
                      max: new Date(classItem?.cancelDate)
                        .toISOString()
                        .split("T")[0],
                    }
                  : {}
              }
              sx={{
                mr: 2,
                mb: isMobile ? 1 : 0,
                mt: 1,
                width: isMobile ? "100%" : "auto",
              }}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={showOldSchedules}
                onChange={(e) => setShowOldSchedules(e.target.checked)}
              />
            }
            label={intl.formatMessage({
              id: "enrollmentModal.showOldSchedules",
            })}
            sx={{ mb: 2 }}
          />
          {renderSchedules(values.scheduleIds, setFieldValue)}
          <ErrorMessage name="scheduleIds" component="div" className="error" />
        </>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={() => {
        clearAllStates();
        setIsPaymentRequired(false);
        onClose();
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : 600,
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            <FormattedMessage
              id="enrollmentModal.title"
              defaultMessage="Enroll Attendee"
            />
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => {
              clearAllStates();
              setIsPaymentRequired(false);
              onClose();
            }}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              {steps.map((step, index) => (
                <Accordion
                  key={index}
                  expanded={activeStep === index}
                  onChange={() => {
                    if (isSafari) {
                      // Safari-specific: Use setTimeout to ensure state updates are processed
                      setTimeout(() => setActiveStep(index), 0);
                    } else {
                      setActiveStep(index);
                    }
                  }}
                  disabled={!canAccessStep(index, values, errors)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{step.title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {step.content(values, setFieldValue, touched, errors)}
                  </AccordionDetails>
                </Accordion>
              ))}
              {backendError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {backendError}
                </Alert>
              )}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
                  disabled={activeStep === 0}
                  sx={{ mb: isMobile ? 1 : 0 }}
                  fullWidth={isMobile}
                >
                  <FormattedMessage id="common.back" defaultMessage="Back" />
                </Button>
                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={() => {
                      if (validateStep(activeStep, values, errors)) {
                        if (isSafari) {
                          // Safari-specific: Use setTimeout to ensure state updates are processed
                          setTimeout(() => {
                            setActiveStep((prev) =>
                              Math.min(steps.length - 1, prev + 1),
                            );
                          }, 0);
                        } else {
                          setActiveStep((prev) =>
                            Math.min(steps.length - 1, prev + 1),
                          );
                        }
                      }
                    }}
                    variant="contained"
                    color="primary"
                    sx={{ mb: isMobile ? 1 : 0 }}
                    fullWidth={isMobile}
                    disabled={!validateStep(activeStep, values, errors)}
                  >
                    <FormattedMessage id="common.next" defaultMessage="Next" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mb: isMobile ? 1 : 0 }}
                    fullWidth={isMobile}
                    disabled={
                      isSubmitting || !validateStep(activeStep, values, errors)
                    }
                  >
                    <FormattedMessage
                      id="enrollmentModal.enroll"
                      defaultMessage="Enroll"
                    />
                  </Button>
                )}
              </Box>
            </Form>
          )}
        </Formik>
        {!Boolean(isAttendance) && (
          <Snackbar
            open={isWaitlistAlert}
            autoHideDuration={6000}
            onClose={() => {
              setIsWaitlistAlert(false);
              modalClose();
              clearAllStates();
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => {
                setIsWaitlistAlert(false);
                modalClose();
                clearAllStates();
              }}
              severity="info"
            >
              <FormattedMessage
                id="enrollmentModal.waitlistAlert"
                defaultMessage="You've been added to the waiting list for some classes."
              />
            </Alert>
          </Snackbar>
        )}

        {/* Payment Required Dialog - Only show for non-admin users */}
        {!isAdmin && (
          <Dialog
            open={isPaymentRequired}
            onClose={() => {
              setIsPaymentRequired(false);
              navigate("/dashboard");
            }}
          >
            <DialogTitle>
              <FormattedMessage
                id="enrollment.payment.title"
                defaultMessage="Payment Required"
              />
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <FormattedMessage id="enrollment.paymentRequired" />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setIsPaymentRequired(false);
                  navigate("/dashboard");
                }}
                variant="contained"
                color="primary"
              >
                <FormattedMessage id="common.ok" defaultMessage="OK" />
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Modal>
  );
};

export default EnrollmentModal;
